import {
  useState,
  useMemo,
  useRef,
  useCallback,
  startTransition,
  useEffect,
} from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  CustomDialog,
  Heading,
  Content,
  Text,
  ButtonGroup,
  Button,
  ActionButtonGroup,
  ActionButton,
  TextArea,
  CloseButton,
  TagGroup,
  Tag,
  FileTrigger,
  Badge,
  TooltipTrigger,
  Tooltip,
  Switch,
  LabeledValue,
} from "@react-spectrum/s2";
import { Pressable } from "react-aria-components";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import AlertTriangleIcon from "@react-spectrum/s2/icons/AlertTriangle";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";
import GoogleGeminiIcon from "icon:@app/assets/GoogleGemini.svg";
import AdobeFireflyIcon from "icon:@app/assets/AdobeFirefly.svg";
import AdobePhotoshopIcon from "icon:@app/assets/AdobePhotoshop.svg";

import { store } from "@app/store";
import { S3Image } from "@app/components/S3Image";
import { S3Video } from "@app/components/S3Video";
import { ReferenceImages } from "./ImageVariations";
import { VariationProgress } from "./VariationProgress";
import { OUTDATED_ACTION } from "./variationDependencies";
import { getVariationOutdatedState } from "./variationOutdatedState";
import { useOutdatedUpdate } from "./useOutdatedUpdate";

import { useDownloadS3Image } from "@app/hooks/s3";
import { useIsAdmin } from "@app/hooks/useRoles";
import {
  REFERENCE_KIND,
  getMissingReferenceKind,
  getVariationType,
  useImageVariation,
} from "@app/hooks/imageVariations";

import {
  style,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

// Wide enough for the "Reference" badge on the references' card.
const REFERENCE_WIDTH = 72;

const CollapsableText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const isCollapsable = text.length > 200;
  const displayText =
    isExpanded || !isCollapsable ? text : `${text.slice(0, 200)}...`;

  return (
    <Text>
      {displayText}
      {isCollapsable && (
        <ActionButton
          size="XS"
          isQuiet
          onPress={() => setIsExpanded(!isExpanded)}
          styles={style({
            marginStart: 4,
          })}
          UNSAFE_style={{
            display: "inline-grid",
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </ActionButton>
      )}
    </Text>
  );
};

CollapsableText.propTypes = {
  text: PropTypes.string,
};

export const EditVariationDialog = ({
  videoId,
  variationKey,
  referenceKey,
  isReferenceAsset,
  dependencies,
  onUpload,
}) => {
  const {
    video,
    template,
    variation: imageVariation,
    asset,
    fallbackAsset,
    referenceAssets,
    progress,
    isGenerating,
    isPromptPending,
    isPromptEdited,
    isStatic,
    staticPrompt,
    isBusy,
    isSkipped,
    setSkipped,
    regenerate,
    regeneratePrompt,
    resetPrompt,
    regenerateFromCampaign,
  } = useImageVariation({
    projectId: videoId,
    variationKey,
    referenceKey,
  });

  const downloadS3Image = useDownloadS3Image();
  const isAdmin = useIsAdmin();

  const colorScheme = useStore(store, (state) => state.colorScheme);
  const progressByKey = useStore(
    store,
    (state) => state.imageVariationProgress,
  );

  // Do not remove, used in eval()
  const selectedCampaign = useMemo(() => {
    return (
      video.data?.campaigns?.find(
        (c) => c.id === video.data?.selectedCampaignIds?.[0],
      ) ?? null
    );
  }, [video.data?.campaigns, video.data?.selectedCampaignIds]);

  // A static prompt is the type's default, shown to admins only: for anyone
  // else the field is blank unless it holds the user's own.
  const defaultPrompt = isStatic && isAdmin ? (staticPrompt ?? "") : "";

  const shownPrompt = (variation) =>
    isStatic && !variation?.promptEdited
      ? defaultPrompt
      : (variation?.visualPrompt ?? "");

  const [prompt, setPrompt] = useState(() => shownPrompt(imageVariation));

  // Text that is not just the default being shown.
  const hasOwnPrompt = !!prompt.trim() && prompt !== defaultPrompt;

  const wasPromptEdited = useRef(!!imageVariation?.promptEdited);
  const lastDefaultPrompt = useRef(defaultPrompt);

  useEffect(() => {
    const next = shownPrompt(imageVariation);

    // The user's own prompt was replaced by the default (e.g. by updating
    // from the campaign). Or the default was shown before the variation was
    // known to be static.
    if (
      isStatic &&
      !imageVariation?.promptEdited &&
      (wasPromptEdited.current ||
        prompt === imageVariation?.visualPrompt ||
        prompt === lastDefaultPrompt.current)
    ) {
      setPrompt(defaultPrompt);
    }

    wasPromptEdited.current = !!imageVariation?.promptEdited;
    lastDefaultPrompt.current = defaultPrompt;

    // For a static one, a refreshed default is handled above — it must not
    // replace what is being typed.
    if (
      (!isStatic || imageVariation?.promptEdited) &&
      next &&
      next !== prompt
    ) {
      setPrompt(next);
    }
  }, [
    imageVariation?.visualPrompt,
    imageVariation?.promptEdited,
    isStatic,
    defaultPrompt,
  ]);

  const handleDownload = useCallback(() => {
    if (!asset?.filePath) return;

    downloadS3Image.mutate({ filePath: asset.filePath, fileName: asset.name });
  }, [asset?.filePath, asset?.name, downloadS3Image.mutate]);

  const handleGenerate = useCallback(() => {
    startTransition(() => {
      regenerate({ prompt: prompt.trim() });
    });
  }, [regenerate, prompt]);

  // A non-generative action (Photoshop) rather than a model: it has no prompt
  // to write or edit, so the prompt controls are hidden and the action's own
  // description is used as the confirm label. Which types those are is
  // configured with the type itself, in `VARIATION_TYPES`.
  const isPhotoshopAction = !!getVariationType(imageVariation?.type)?.action;

  const hasEditedPrompt =
    isPromptEdited ||
    (isStatic
      ? hasOwnPrompt
      : prompt.trim() !== (imageVariation?.visualPrompt ?? ""));
  const {
    isOutdated,
    action: outdatedAction,
    message: outdatedMessage,
  } = getVariationOutdatedState({
    variation: {
      ...imageVariation,
      key: variationKey,
      referenceKey: referenceKey ?? imageVariation?.referenceKey,
    },
    dependencies,
    assets: video.data?.assets,
    selectedCampaign,
    video,
    templateData: template,
    isPromptEdited: hasEditedPrompt,
    isBusy: isGenerating || isBusy || isPromptPending || !!asset?.isLoading,
  });

  // What is wrong with the preview: a failed generation, else a campaign or
  // references that have changed since it was generated.
  const notice =
    imageVariation?.errorMessage || (isOutdated ? outdatedMessage : null);

  // Outdated by a campaign its prompt has to be rewritten for: the primary
  // action writes a new prompt and image, and the prompt as it is can still be
  // kept. A prompt edited here but not generated from yet is the user's own
  // too.
  const isUpdatingPrompt =
    isOutdated &&
    !isPhotoshopAction &&
    outdatedAction === OUTDATED_ACTION.PROMPT_AND_IMAGE;

  const { update, confirmDialog } = useOutdatedUpdate({
    action: outdatedAction,
    isPromptEdited: hasEditedPrompt,
    regenerateFromCampaign,
    onKeepPrompt: handleGenerate,
  });

  // As on `ImageVariationCard`: with no real progress to report, a preview
  // that has nothing to show yet is still "generating" — unless it failed.
  const hasPreview = !!(isSkipped ? fallbackAsset : asset)?.filePath;

  const isShowingProgress =
    !!progress ||
    (!imageVariation?.errorCode &&
      !imageVariation?.errorMessage &&
      !hasPreview);

  const isGenerateDisabled =
    isShowingProgress ||
    isGenerating ||
    isReferenceAsset ||
    isSkipped ||
    isBusy ||
    isPromptPending ||
    !!asset?.isLoading;

  // A static variation generates from its default prompt when the field is blank.
  const isPromptMissing = !isStatic && !prompt.trim();

  // Clears what was typed, and the prompt the pipeline kept for it.
  const handleRegeneratePrompt = () => {
    if (isStatic) {
      setPrompt(defaultPrompt);
      resetPrompt();
    } else {
      regeneratePrompt();
    }
  };

  // Rendered beside the prompt input normally, and in the footer next to the
  // confirm button for prompt-less actions (which have no input to sit next to).
  // A reference being (re)generated has no image for a while, but it is still
  // there — shown loading; one that is only a template asset nobody selected is
  // not.
  const generatedReferences = referenceAssets.filter(
    (reference) =>
      !!reference?.filePath ||
      getMissingReferenceKind(reference?.name, template) ===
        REFERENCE_KIND.GENERATED,
  );

  const hasReference = generatedReferences.length > 0;

  const referenceThumbnail = !hasReference ? null : (
    <LabeledValue
      label="Reference"
      // `contain: inline-size` would otherwise size it to a field's width.
      styles={style({ width: REFERENCE_WIDTH, flexShrink: 0 })}
      value={
        <ReferenceImages
          assets={generatedReferences}
          templateData={template}
          showTooltips
          progressByKey={progressByKey}
          // Describing the image is the images steps' concern, not this dialog's.
          skipImageLabeling
          colorScheme={colorScheme}
          aspectRatio="1 / 1"
          showBadge={false}
          // Too small for a label.
          showProgressLabel={false}
        />
      }
    />
  );

  const model = (() => {
    const model = fallbackAsset?.model ?? asset?.model;
    let ret = null;

    switch (model) {
      case "nano-banana":
      case "nano-banana-2":
      case "nano-banana-3":
        ret = {
          model: model,
          icon: GoogleGeminiIcon,
          label: `Gemini_${model}`,
        };
        break;
      case "image4_ultra":
      case "image5":
      case "video":
        ret = {
          model: model,
          icon: AdobeFireflyIcon,
          label: `Firefly_${model}`,
        };
        break;
      case "photoshop":
        ret = {
          model: model,
          icon: AdobePhotoshopIcon,
          label: "Photoshop",
        };
        break;
      default:
        break;
    }

    if (ret?.label) {
      ret.label = ret.label
        .split(/[_-]|(?<=\w)(?=\d)/)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
    }

    return ret;
  })();

  return (
    <CustomDialog
      padding="none"
      styles={style({
        width: 1600,
        maxWidth: "90vw",
      })}
    >
      {({ close }) => (
        <div
          className={style({
            display: "grid",
            size: "full",
            maxHeight: { lg: "90vh", default: "full" },
            overflow: "hidden",
            gridTemplateAreas: {
              lg: ["visual . . .", "visual . content .", "visual . . ."],
              default: [
                "visual visual visual",
                ". . .",
                ". content .",
                ". . .",
              ],
            },
            gridTemplateColumns: {
              lg: ["1fr", 24, "minmax(auto, 480px)", 24],
              default: [24, "auto", 24],
            },
            gridTemplateRows: {
              lg: [24, "minmax(0, 1fr)", 24],
              default: ["auto", 24, "1fr", 24],
            },
          })}
        >
          <div
            className={style({
              gridArea: "visual",
              position: "relative",
              size: "full",
            })}
          >
            {asset?.type === "video" && !isSkipped ? (
              <S3Video
                filePath={asset?.filePath}
                src={asset?.src}
                isLoading={!asset?.filePath}
                className={style({
                  display: "block",
                  objectFit: "cover",
                  width: "full",
                  aspectRatio: "16 / 9",
                })}
                controls
                autoPlay
                loop
              />
            ) : (
              <S3Image
                alt={asset?.name ?? ""}
                filePath={isSkipped ? fallbackAsset?.filePath : asset?.filePath}
                isLoading={
                  isSkipped ? !fallbackAsset?.filePath : !asset?.filePath
                }
                styles={style({
                  size: "full",
                  objectFit: "contain",
                })}
                UNSAFE_style={
                  !asset?.filePath ? { aspectRatio: "1 / 1" } : undefined
                }
              />
            )}
            {isShowingProgress && (
              <div
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  size: "full",
                  position: "absolute",
                  top: 0,
                  insetStart: 0,
                })}
              >
                <VariationProgress progress={progress} size={32} />
              </div>
            )}
            {!!notice && (
              <Badge
                variant="notice"
                size="M"
                styles={style({
                  position: "absolute",
                  top: 12,
                  insetStart: 12,
                })}
              >
                <AlertTriangleIcon />
              </Badge>
            )}

            {!!model && (
              <TooltipTrigger placement="start" delay={0}>
                <Pressable>
                  <div
                    role="button"
                    className={style({
                      position: "absolute",
                      top: 12,
                      insetEnd: 12,
                    })}
                  >
                    <model.icon styles={style({ size: 24 })} />
                  </div>
                </Pressable>
                <Tooltip>
                  <Text>{model.label}</Text>
                </Tooltip>
              </TooltipTrigger>
            )}
          </div>
          <div
            className={style({
              gridArea: "content",
              display: "flex",
              flexDirection: "column",
              gap: 24,
              size: "full",
              overflowY: "auto",
            })}
          >
            <Heading
              slot="title"
              styles={style({
                gridArea: "heading",
                font: "heading",
                margin: 0,
              })}
            >
              {imageVariation?.label ??
                `${!!asset?.prompt ? "Edit variation: " : ""}${variationKey}`}
              {!!imageVariation?.key && (
                <>
                  <br />
                  <Text
                    styles={style({
                      fontSize: "ui",
                      lineHeight: "[1]",
                      color: "disabled",
                      whiteSpace: "nowrap",
                    })}
                  >
                    ({imageVariation?.key})
                  </Text>
                </>
              )}
            </Heading>
            <Content
              styles={style({
                display: "flex",
                flexDirection: "column",
                gap: 16,
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: "[0%]",
              })}
            >
              {[
                [null, imageVariation?.description],
                ["Tagline", imageVariation?.tagline],
                ["Audience", imageVariation?.audience],
                ...((imageVariation?.context ?? []).map((c) => [
                  c.label,
                  eval(c.value),
                ]) ?? []),
                ["AI Summary", asset?.labeling?.description],
              ].map(([label, value], idx) =>
                !!value ? (
                  <div key={label ?? idx}>
                    {!!label && (
                      <div
                        className={style({
                          font: "detail",
                          color: "neutral-subdued",
                          marginBottom: 4,
                        })}
                      >
                        {label}
                      </div>
                    )}
                    <div className={style({ font: "body" })}>
                      <CollapsableText text={value} />
                    </div>
                  </div>
                ) : null,
              )}
              {[
                [
                  "Main subject",
                  !!asset?.labeling?.mainSubject && [
                    asset?.labeling?.mainSubject,
                  ],
                ],
                ["Attributes", asset?.labeling?.attributes],
                ["Key elements", asset?.labeling?.keyElements],
              ].map(([label, value]) =>
                !!value && Array.isArray(value) ? (
                  <TagGroup
                    key={label}
                    label={label}
                    size="XS"
                    selectionMode="none"
                    maxRows={1}
                  >
                    {value.map((el, i) => (
                      <Tag key={i}>{el}</Tag>
                    ))}
                  </TagGroup>
                ) : null,
              )}
              <div>
                <ActionButtonGroup
                  orientation="horizontal"
                  styles={style({
                    justifySelf: "start",
                    marginTop: {
                      default: 32,
                      isAlone: "auto",
                    },
                  })({
                    isAlone: isReferenceAsset,
                  })}
                >
                  {!isReferenceAsset && (
                    <FileTrigger
                      acceptedFileTypes="image/*,video/*"
                      onSelect={(files) => onUpload?.(files, asset?.name)}
                    >
                      <ActionButton
                        isDisabled={
                          !asset?.filePath || downloadS3Image.isPending
                        }
                      >
                        <UploadToCloudIcon />
                        <Text>
                          Upload own{" "}
                          {asset?.type === "video" ? "video" : "image"}
                        </Text>
                      </ActionButton>
                    </FileTrigger>
                  )}
                  <ActionButton
                    isDisabled={!asset?.filePath || downloadS3Image.isPending}
                    onPress={handleDownload}
                  >
                    <DownloadIcon />
                    <Text>
                      Download {asset?.type === "video" ? "video" : "image"}
                    </Text>
                  </ActionButton>
                </ActionButtonGroup>
              </div>
              {!isReferenceAsset && (
                <div
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    marginTop: "auto",
                    paddingTop: 24,
                  })}
                >
                  {!isPhotoshopAction && (
                    <ActionButton
                      onPress={handleRegeneratePrompt}
                      isDisabled={
                        isGenerateDisabled ||
                        (isStatic && !hasOwnPrompt && !isPromptEdited)
                      }
                      isPending={!isStatic && isPromptPending}
                      size="XS"
                      styles={style({ marginStart: "auto", marginBottom: -16 })}
                    >
                      <AIGenerateIcon />
                      <Text>Regenerate prompt</Text>
                    </ActionButton>
                  )}
                  <div
                    className={style({
                      display: "flex",
                      gap: 12,
                      alignItems: "end",
                    })}
                  >
                    {!isPhotoshopAction && !!referenceThumbnail && (
                      <div
                        className={style({
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        })}
                      >
                        {referenceThumbnail}
                      </div>
                    )}
                    {!isPhotoshopAction && (
                      <TextArea
                        label={`Prompt`}
                        placeholder={
                          isStatic
                            ? "Enter your prompt or leave blank for default"
                            : ""
                        }
                        value={prompt}
                        onChange={setPrompt}
                        isDisabled={isGenerateDisabled}
                        styles={style({ flexGrow: 1 })}
                        UNSAFE_className="__PromptInput __PromptInput--narrow"
                      />
                    )}
                  </div>
                  {notice ? (
                    <div
                      className={style({
                        display: "inline-flex",
                        marginTop: 8,
                        marginStart: {
                          isIntented: 96,
                          default: 0,
                        },
                      })({ isIntented: hasReference })}
                    >
                      <Text
                        styles={style({ font: "ui-sm", color: "notice-900" })}
                      >
                        <AlertTriangleIcon
                          styles={iconStyle({
                            size: "S",
                            color: "notice-900",
                            marginEnd: 4,
                          })}
                          UNSAFE_className={style({
                            verticalAlign: "text-bottom",
                          })}
                        />
                        {notice}
                      </Text>
                    </div>
                  ) : null}
                </div>
              )}
            </Content>
            <ButtonGroup>
              {isPhotoshopAction && !!referenceThumbnail && (
                <div
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  })}
                >
                  {referenceThumbnail}
                </div>
              )}
              {!isReferenceAsset && !!imageVariation?.fallbackKey && (
                <Switch
                  aria-label="Use fallback asset"
                  isSelected={isSkipped}
                  isEmphasized
                  onChange={setSkipped}
                >
                  <Text slot="description">Use fallback asset</Text>
                </Switch>
              )}
              {!isReferenceAsset && isUpdatingPrompt && (
                <Button
                  variant="secondary"
                  onPress={() => handleGenerate()}
                  isDisabled={isGenerateDisabled || isPromptMissing}
                  styles={style({ marginStart: "auto", alignSelf: "end" })}
                >
                  <Text>Keep prompt</Text>
                </Button>
              )}
              {!isReferenceAsset && isUpdatingPrompt && (
                <Button
                  variant="primary"
                  onPress={update}
                  isEmphasized
                  isDisabled={isGenerateDisabled}
                  styles={style({ alignSelf: "end" })}
                >
                  <AIGenerateIcon />
                  <Text>Update & generate</Text>
                </Button>
              )}
              {!isReferenceAsset && !isUpdatingPrompt && (
                <Button
                  variant="primary"
                  onPress={() => handleGenerate()}
                  isEmphasized
                  isDisabled={isGenerateDisabled || isPromptMissing}
                  styles={style({ marginStart: "auto", alignSelf: "end" })}
                >
                  <AIGenerateIcon />
                  <Text>
                    {isPhotoshopAction
                      ? prompt.trim() || "Apply"
                      : hasEditedPrompt
                        ? "Generate"
                        : "Regenerate"}
                  </Text>
                </Button>
              )}
            </ButtonGroup>
            {confirmDialog}
          </div>
          <CloseButton
            styles={style({ position: "absolute", top: 12, insetEnd: 12 })}
          />
        </div>
      )}
    </CustomDialog>
  );
};

EditVariationDialog.propTypes = {
  videoId: PropTypes.string.isRequired,
  variationKey: PropTypes.string.isRequired,
  referenceKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  isReferenceAsset: PropTypes.Boolean,
  dependencies: PropTypes.shape({
    keys: PropTypes.arrayOf(PropTypes.string),
    labels: PropTypes.instanceOf(Map),
  }),
  onUpload: PropTypes.func.isRequired,
};
